import * as d3 from "d3";
import { addTooltip } from "@/components/VivoPlots/functions/utils/tooltipFunctions";
import { tooltipPosition } from "../utils/tickTooltip";
import {
  getBoxDimensions,
  MIN_HOVER_AREA_HEIGHT,
} from "../utils/boxDimensions";

const boxplotStroke = "#556C75";

export function drawBoxPlot(
  plotClassName: any,
  data: any,
  scales: any[],
  plotConfig: any, // eslint-disable-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars
) {
  const continuousScale = scales?.find((scale) => scale?.type === "continuous");
  const categoricalScale = scales?.find(
    (scale) => scale?.type === "categorical",
  );
  const subgroupScale = scales?.find(
    (scale) => scale?.type === "subgroup" && scale?.dataMapping !== "none",
  );

  const tooltip = addTooltip(plotClassName);

  const scaleWidth =
    subgroupScale?.scale?.bandwidth() || categoricalScale?.scale.bandwidth();

  const scalePosition = (d: any) =>
    categoricalScale?.scale(d.category) +
    (subgroupScale?.scale(d.subgroup) || 0);

  const boxWidth = () => d3.min([scaleWidth, 180]) ?? 0;

  const boxCenter = (d: any) => scalePosition(d) + scaleWidth / 2;

  const boxPosition = (d: any) => boxCenter(d) - boxWidth() / 2;

  const boxContainer = d3
    .select(`svg.${plotClassName}`)
    .selectAll("g.box")

    .data(data, (d: any) => `${d.category}|${d.subgroup}`)
    .join("g")
    .classed("box", true);

  // main boxplot box
  boxContainer
    .selectAll("rect.box-main")
    .data((d) => [d])
    .join("rect")
    .attr("class", "box-main")
    .attr("x", (d) => boxPosition(d))
    .attr("y", (d) => getBoxDimensions(d, continuousScale).y)
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    .attr("width", (d) => boxWidth())
    .style("opacity", 0.6)
    .attr("height", (d) => getBoxDimensions(d, continuousScale).height)
    .attr("stroke-width", 1)
    .attr("fill", "white")
    .attr("stroke", boxplotStroke)
    .attr("rx", 2);

  // median line
  boxContainer
    .selectAll("line.box-median")
    .data((d) => [d])
    .join("line")
    .attr("class", "box-median")

    .attr("x1", (d: any) => boxPosition(d))

    .attr("x2", (d: any) => boxPosition(d) + boxWidth())

    .attr("y1", (d: any) => continuousScale.scale(d.stats.median))
    .attr("stroke-width", 1)

    .attr("y2", (d: any) => continuousScale.scale(d.stats.median))
    .attr("stroke", boxplotStroke)
    .style("opacity", 0.6);

  // lower whisker
  boxContainer
    .selectAll("line.box-lower-whisker")
    .data((d) => [d])
    .join("line")
    .attr("class", "box-lower-whisker")

    .attr("x1", (d: any) => boxPosition(d) + boxWidth() / 2)

    .attr("x2", (d: any) => boxPosition(d) + boxWidth() / 2)

    .attr("y1", (d: any) => continuousScale.scale(d.stats.q1))

    .attr("y2", (d: any) => continuousScale.scale(d.stats.lowerWhisker))
    .attr("stroke-width", 1)
    .attr("stroke", boxplotStroke)
    .style("opacity", 0.6);

  // upper whisker
  boxContainer
    .selectAll("line.box-upper-whisker")
    .data((d) => [d])
    .join("line")
    .attr("class", "box-upper-whisker")

    .attr("x1", (d: any) => boxPosition(d) + boxWidth() / 2)

    .attr("x2", (d: any) => boxPosition(d) + boxWidth() / 2)

    .attr("y1", (d: any) => continuousScale.scale(d.stats.q3))

    .attr("y2", (d: any) => continuousScale.scale(d.stats.upperWhisker))
    .attr("stroke-width", 1)
    .attr("stroke", boxplotStroke)
    .style("opacity", 0.6);

  // Invisible hit area for hover - uses main box height (Q1 to Q3) with minimum height
  // This ensures hovering works even when boxplot is reduced to a thin line (single subject)
  const hoverArea = boxContainer
    .selectAll("rect.box-hover-area")
    .data((d) => [d])
    .join("rect")
    .attr("class", "box-hover-area")
    .attr("x", (d) => boxPosition(d))
    .attr(
      "y",
      (d) => getBoxDimensions(d, continuousScale, MIN_HOVER_AREA_HEIGHT).y,
    )
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    .attr("width", (d) => boxWidth())
    .attr(
      "height",
      (d) => getBoxDimensions(d, continuousScale, MIN_HOVER_AREA_HEIGHT).height,
    )
    .attr("fill", "transparent");

  //tooltip
  hoverArea
    .on("mouseover", function (event, d: any) {
      tooltip.transition().duration(50).style("opacity", 0.9);
      const container = d3.select(`.plot-container.${plotClassName}`);
      const pos = tooltipPosition(
        event,
        container as unknown as d3.Selection<HTMLElement, unknown, any, any>,
        tooltip as unknown as d3.Selection<HTMLDivElement, unknown, any, any>,
      );
      if (!pos) return;
      const { left, top } = pos;
      tooltip
        .style("z-index", 1000)

        .html(
          `
          <ul>
            <li><span class='font-bold'>Sample Size:</span> ${
              d.stats.total
            }</li>
            <li><span class='font-bold'>Mean:</span> ${
              Math.round(d.stats.mean * 100) / 100
            }</li>
            <li><span class='font-bold mb-[10px]'>Standard Deviation:</span> ${
              Math.round(d.stats.std * 100) / 100
            }</li>
            <li class="text-[12px]">97.5%: ${
              Math.round(d.stats.upperWhisker * 100) / 100
            }</li>
            <li class="text-[12px]">Third Quantile: ${
              Math.round(d.stats.q3 * 100) / 100
            }</li>
            <li class="text-[12px]">Median: ${
              Math.round(d.stats.median * 100) / 100
            }</li>
            <li class="text-[12px]">First Quantile: ${
              Math.round(d.stats.q1 * 100) / 100
            }</li>
            <li class="text-[12px]">2.5%: ${
              Math.round(d.stats.lowerWhisker * 100) / 100
            }</li>
          </ul>
          `,
        )
        .style("left", left + "px")
        .style("top", top + "px");
    })
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    .on("mouseout", function (event) {
      tooltip.transition().duration(50).style("opacity", 0);
    })
    .on("mousemove", function (event) {
      const container = d3.select(`.plot-container.${plotClassName}`);
      const pos = tooltipPosition(
        event,
        container as unknown as d3.Selection<HTMLElement, unknown, any, any>,
        tooltip as unknown as d3.Selection<HTMLDivElement, unknown, any, any>,
      );
      if (!pos) return;
      const { left, top } = pos;
      tooltip.style("left", left + "px").style("top", top + "px");
    });
}
